import React from "react";
import {
    Box, Flex, Icon, Input, Tag, TagCloseButton, TagLabel, Text, Wrap, WrapItem, useColorModeValue,
} from "@chakra-ui/react";
import { MdWarning } from "react-icons/md";

const SUGGESTED_FIELDS = [
    "RIB",
    "Photo recto identité",
    "Photo verso identité",
    "Justificatif de domicile",
    "Photo lisible",
    "Nom complet",
    "Date de naissance",
    "Lien réseau social"
];
const DATALIST_ID = "missing-fields-suggestions";

/**
 * Trims and removes empty/duplicate entries from the field list.
 * @param {Array<string>} list - Raw field values
 * @returns {Array<string>} Cleaned list
 */
function normalizeFields(list) {
    const seen = new Set();
    const out = [];
    for (const raw of list) {
        const value = (raw || "").trim();
        if (!value || seen.has(value)) continue;
        seen.add(value);
        out.push(value);
    }
    return out;
}

/**
 * Adds a new entry to the field list and resets the input.
 * @param {Object} args - { event, fields, onChange }
 */
function commitFieldEntry({ event, fields, onChange }) {
    if (event.key !== "Enter") return;
    event.preventDefault();
    const value = event.target.value.trim();
    if (!value) return;
    onChange(normalizeFields([...(fields || []), value]));
    event.target.value = "";
}

/**
 * Warning header with the current count of missing fields.
 * @param {Object} props
 * @param {number} props.count - Current number of missing fields
 * @returns {React.ReactElement}
 */
function EditorHeader({ count }) {
    return (
        <Flex align="center" gap="6px">
            <Icon as={MdWarning} color="orange.400" w="16px" h="16px" />
            <Text color="orange.400" fontSize="sm" fontWeight="600">
                Champs manquants {count > 0 ? `(${count})` : ""}
            </Text>
        </Flex>
    );
}

/**
 * Renders the removable tag list of selected missing fields.
 * @param {Object} props
 * @param {Array<string>} props.fields - Current field list
 * @param {Function} props.onChange - Receives the updated list
 * @returns {React.ReactElement|null}
 */
function FieldTags({ fields, onChange }) {
    if (!fields || fields.length === 0) return null;
    const removeField = (field) => onChange(fields.filter((f) => f !== field));
    return (
        <Wrap spacing="6px" mt="10px">
            {fields.map((field, idx) => (
                <WrapItem key={idx}>
                    <Tag colorScheme="orange" borderRadius="full" size="md">
                        <TagLabel>{field}</TagLabel>
                        <TagCloseButton onClick={() => removeField(field)} />
                    </Tag>
                </WrapItem>
            ))}
        </Wrap>
    );
}

/**
 * Editable list of missing certification fields. Renders existing entries as
 * removable tags and a free-text input to add new ones (datalist suggestions
 * are hints only; any string is accepted on Enter).
 * @param {Object} props
 * @param {Array<string>} props.fields - Current list of missing fields
 * @param {Function} props.onChange - Receives the updated list of strings
 * @returns {React.ReactElement}
 */
function MissingFieldsEditor({ fields, onChange }) {
    const bg = useColorModeValue("orange.50", "whiteAlpha.100");
    const hintColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box p="14px" bg={bg} borderRadius="12px" border="1px solid" borderColor="orange.200">
            <EditorHeader count={(fields || []).length} />
            <FieldTags fields={fields} onChange={onChange} />
            <Input mt="10px" size="sm" borderRadius="8px" list={DATALIST_ID}
                placeholder="Ajouter un champ manquant puis Entrée"
                onKeyDown={(event) => commitFieldEntry({ event, fields, onChange })} />
            <datalist id={DATALIST_ID}>
                {SUGGESTED_FIELDS.map((s) => <option key={s} value={s} />)}
            </datalist>
            <Text mt="6px" color={hintColor} fontSize="xs" fontStyle="italic">
                Cliquez sur le « × » d'une chip pour la retirer. Tapez puis Entrée pour ajouter une entrée libre.
            </Text>
        </Box>
    );
}

export default MissingFieldsEditor;
